import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import {
  HIDDEN_KEYS,
  filterVisible,
  hideItem,
} from "../utils/hiddenHistory";
import {
  formatAppDate,
  todayLocalDateString,
} from "../utils/dateFormat";

const defaultLoan = () => ({
  customer_id: "",
  amount: "",
  loan_date: todayLocalDateString(),
  type: "given",
  notes: "",
});

const typeLabel = (type) => {
  if (type === "received") return "Received";
  if (type === "given") return "Given";
  return type || "-";
};

export default function Loan() {
  const [customers, setCustomers] = useState([]);
  const [loans, setLoans] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loan, setLoan] = useState(defaultLoan);
  const [loading, setLoading] = useState(true);
  const [showNewLoan, setShowNewLoan] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [listVersion, setListVersion] = useState(0);
  const [showCustomerLoans, setShowCustomerLoans] = useState(false);
  const [loanCustomer, setLoanCustomer] = useState(null);
  const [customerLoans, setCustomerLoans] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const formatNumber = (value) => Number(value ?? 0).toFixed(2);

  const fetchData = async () => {
    setFetchError("");
    try {
      const customersRes = await axios.get(`${API_BASE_URL}/api/customers`);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (error) {
      console.error("Error fetching customers", error);
      setFetchError("Could not load customers. Check that the API is running.");
    }

    try {
      const loansRes = await axios.get(`${API_BASE_URL}/api/loans`);
      setLoans(Array.isArray(loansRes.data) ? loansRes.data : []);
    } catch (error) {
      console.error("Error fetching loans", error);
      setLoans([]);
      const msg =
        error.response?.data?.message ||
        (error.response?.status === 500
          ? "Loans table may be missing. Run: php artisan migrate in the backend folder."
          : "Could not load loans.");
      setFetchError((prev) => prev || msg);
    } finally {
      setLoading(false);
    }
  };

  const visibleLoans = filterVisible(loans, HIDDEN_KEYS.loans);
  void listVersion;

  const getCustomerLoanStats = (cust) => {
    const custLoans = visibleLoans.filter(
      (item) => String(item.customer_id) === String(cust.id),
    );
    const given = custLoans
      .filter((item) => item.type === "given")
      .reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
    const received = custLoans
      .filter((item) => item.type === "received")
      .reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
    return {
      given,
      received,
      balance: given - received,
      count: custLoans.length,
    };
  };

  const customerRows = customers.map((cust) => ({
    ...cust,
    ...getCustomerLoanStats(cust),
  }));

  const filteredCustomers = selectedCustomer
    ? customerRows.filter((cust) => String(cust.id) === String(selectedCustomer))
    : customerRows;

  const totalGiven = filteredCustomers.reduce((sum, c) => sum + c.given, 0);
  const totalReceived = filteredCustomers.reduce((sum, c) => sum + c.received, 0);
  const totalBalance = filteredCustomers.reduce((sum, c) => sum + c.balance, 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoan({ ...loan, [name]: value });
  };

  const openCustomerLoans = (cust) => {
    setLoanCustomer(cust);
    setShowCustomerLoans(true);
    const custLoans = visibleLoans.filter(
      (item) => String(item.customer_id) === String(cust.id),
    );
    setCustomerLoans(custLoans);
  };

  const openAddLoan = (cust = null) => {
    setLoan({
      ...defaultLoan(),
      customer_id: cust ? String(cust.id) : "",
    });
    setShowNewLoan(true);
  };

  const closeNewLoan = () => {
    setLoan(defaultLoan());
    setShowNewLoan(false);
  };

  const deleteLoan = (id) => {
    if (
      !window.confirm(
        "Remove this loan from the list? Restore it from the History page.",
      )
    ) {
      return;
    }
    hideItem(HIDDEN_KEYS.loans, id);
    setListVersion((v) => v + 1);
    setCustomerLoans((prev) => prev.filter((item) => String(item.id) !== String(id)));
    alert("Loan moved to History. Database is not changed.");
  };

  const saveLoan = async (e) => {
    e.preventDefault();
    if (!loan.customer_id) {
      alert("Select a customer for the loan");
      return;
    }
    const amount = Number(loan.amount);
    if (!Number.isFinite(amount) || amount < 0.01) {
      alert("Enter an amount of at least 0.01");
      return;
    }
    const payload = {
      customer_id: Number(loan.customer_id),
      amount,
      loan_date: loan.loan_date,
      type: loan.type,
      notes: loan.notes?.trim() || null,
    };
    try {
      await axios.post(`${API_BASE_URL}/api/loans`, payload);
      alert("Loan recorded successfully");
      closeNewLoan();
      await fetchData();
      if (loanCustomer && String(loanCustomer.id) === String(payload.customer_id)) {
        const loansRes = await axios.get(`${API_BASE_URL}/api/loans`);
        const all = Array.isArray(loansRes.data) ? loansRes.data : [];
        setCustomerLoans(
          filterVisible(
            all.filter((item) => String(item.customer_id) === String(loanCustomer.id)),
            HIDDEN_KEYS.loans,
          ),
        );
      }
    } catch (error) {
      console.error("Error recording loan", error);
      const data = error.response?.data;
      const validationMsg = data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : null;
      alert(
        validationMsg ||
          data?.message ||
          "Failed to record loan. Check required fields.",
      );
    }
  };

  if (loading) {
    return <Loader message="Loading loans…" fullPage />;
  }

  return (
    <>
      <div className="container-fluid app-container">
        {fetchError ? (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        ) : null}
        <div className="row mb-3">
          <div className="col-12 app-action-bar d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
            <button
              type="button"
              className="btn btn-primary app-btn"
              onClick={() => openAddLoan()}
            >
              + Add Loan
            </button>
            <div className="ms-md-auto w-100 w-md-auto">
              <label className="form-label visually-hidden" htmlFor="loan-customer-filter">
                Filter customers
              </label>
              <select
                id="loan-customer-filter"
                className="form-select"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">All customers</option>
                {customers.length === 0 ? (
                  <option value="" disabled>
                    No customers available
                  </option>
                ) : (
                  customers.map((cust) => (
                    <option key={cust.id} value={cust.id}>
                      {cust.name}
                      {cust.place ? ` (${cust.place})` : ""}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="card app-screen-card">
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3">
                <h4 className="app-card-title mb-2 mb-md-0">Customer Loans</h4>
                <div className="app-sales-stats small">
                  <strong>Customers:</strong> {filteredCustomers.length}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>Given:</strong> {formatNumber(totalGiven)}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>Received:</strong> {formatNumber(totalReceived)}
                  <span className="app-sales-stats__sep"> · </span>
                  <strong>Balance:</strong> {formatNumber(totalBalance)}
                </div>
              </div>
              <div className="table-responsive app-table-wrap">
                <table className="table table-sm app-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Place</th>
                      <th>Given</th>
                      <th>Received</th>
                      <th>Balance</th>
                      <th>Entries</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No customers found.
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((cust) => (
                        <tr key={cust.id}>
                          <td data-label="Name">{cust.name}</td>
                          <td data-label="Place">
                            {cust.place ?? cust.type ?? "-"}
                          </td>
                          <td data-label="Given">{formatNumber(cust.given)}</td>
                          <td data-label="Received">
                            {formatNumber(cust.received)}
                          </td>
                          <td data-label="Balance" className="cell-highlight">
                            {formatNumber(cust.balance)}
                          </td>
                          <td data-label="Entries">{cust.count}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openCustomerLoans(cust)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCustomerLoans && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCustomerLoans(false);
              setCustomerLoans([]);
              setLoanCustomer(null);
            }
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-customer-loans-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="modal-customer-loans-title">
                {loanCustomer?.name || "Customer"} Loans
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={() => {
                  setShowCustomerLoans(false);
                  setCustomerLoans([]);
                  setLoanCustomer(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="app-modal__body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <strong>Place:</strong> {loanCustomer?.place ?? "-"}
                </div>
               
              </div>
              <div className="mb-3">
                <strong>Total entries:</strong> {customerLoans.length}
              </div>
              {customerLoans.length === 0 ? (
                <p className="text-muted mb-0">No loans recorded for this customer.</p>
              ) : (
                <div className="table-responsive app-table-wrap">
                  <table className="table table-sm app-data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Notes</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerLoans.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDate(item.loan_date)}
                          </td>
                          <td data-label="Type">
                            <span
                              className={
                                item.type === "given"
                                  ? "badge text-bg-warning"
                                  : "badge text-bg-success"
                              }
                            >
                              {typeLabel(item.type)}
                            </span>
                          </td>
                          <td data-label="Amount" className="cell-highlight">
                            {formatNumber(item.amount)}
                          </td>
                          <td data-label="Notes">{item.notes || "-"}</td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteLoan(item.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showNewLoan && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeNewLoan();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-loan-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="new-loan-modal-title">
                Add Loan
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeNewLoan}
              >
                ×
              </button>
            </div>
            <form onSubmit={saveLoan}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Customer</label>
                  <select
                    className="form-select"
                    name="customer_id"
                    value={loan.customer_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                        {customer.place ? ` (${customer.place})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    name="type"
                    value={loan.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="given">Given (lent to customer)</option>
                    <option value="received">Received (repayment)</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Loan Date</label>
                  <input
                    className="form-control"
                    type="date"
                    name="loan_date"
                    value={loan.loan_date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min="0.01"
                    name="amount"
                    value={loan.amount}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={loan.notes}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Remark or reference"
                  />
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeNewLoan}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Loan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
